import { APP } from '../globals';
import { ImageLoader, Queue } from './loaders';
import { Sprite } from '../display';
import Asset from './Asset';

function src2name(src) {
    var name = src.split('/');
    name = name.pop();
    name = name.split('.');
    name = name.shift() + '';
    return name;
}

/**
 * @class
 * @classdesc Класс для предзагрузки растра и его использования
 * @memberof module:media
 */
class AssetsLibrary {

    /**
     * @constructor
     * @param {String} [path] Относительный путь к изображениям
     * @param {float} [scale] Используемый масштаб
     * @param {Array} [assets] Необязательный массив описаний для загрузки (см. <a href="Asset.html">Asset</a>). Удобно использовать, если эти данные определяются в отдельном JS.
     */
    constructor(path = 'assets/images', scale = APP.layout.bitmapScale, version = '', assets = []) {

        /**
         * Папка с картинками
         * @type {string}
         * @ignore
         */
        this.path = path;

        /**
         * Asset version
         * @type {string}
         */
        this.version = version;

        /**
         * Масштаб
         * @type {float}
         * @ignore
         */
        this.scale = scale;

        /**
         * @description Массив описаний мувиков
         * @type {Object}
         * @ignore
         */
        this.items = new Map();

        /**
         * @description Флаг полной загрузки последней очереди.
         * @type {boolean}
         */
        this.loaded = false;

        /**
         * @description Класс спрайта, который будет возвращаться библиотекой по умолчанию
         * @type {Function}
         */
        this.spriteClass = Sprite;

        this.addAssets(assets);
    }

    /**
     * Создаёт очередь загрузки изображений для {@link module:media.module:queue.Loader Loader}
     * @return {module:media.module:queue.Queue}
     */
    createLoaderQueue(assets) {

        this.addAssets(assets);

        let self = this;
        let queue = new Queue();
        for (let [name, asset] of this.items) {
            let ldr = new ImageLoader(asset.src);
            ldr.name = asset.name || null;
            queue.add(ldr);
        }

        queue.on('fileload', (e) => {
            let asset = this.items.get(e.item.name);
            asset.bitmap = e.item.data;
        });

        return queue;
    }

    /**
     * Добавляет набор картинок
     * @param {Array} data Набор картинок, массив объектов-описаний, например из конфига приложения.
     * @returns {void}
     */
    addAssets(data) {
        if (typeof data == 'undefined') return;
        if (typeof data != 'object') return;

        for (let item of data) {
            item.noscale = (typeof item.noscale == 'undefined') ? false : item.noscale;
            if (!item.noscale) item.src = '%APP.layout.bitmapScale%/' + item.src;
            if (this.version !== '') item.src += `?version=${this.version}`;
            this.addAsset(item);
        }
    }

    /**
     * Ставит мувик в очередь на загрузку
     * @param {String} src Путь к картинке. Используйте %APP.layout.bitmapScale%/path/asset.ext для картинок с масштабом
     * @param {String} [name] Название. Если не указано, будет использовано src без расширения.
     * @param {Number} [w] Ширина в масштабе 1x. Если не указана, будет произведена попытка определить автоматически (Внимание! Автоопределение не везде поддерживается)
     * @param {Number} [h] Высота в масштабе 1x. Если не указана, будет произведена попытка определить автоматически (Внимание! Автоопределение не везде поддерживается)
     * @param {Number} [f=1] Количество фреймов (вертикально)
     * @param {Number} [l=1] Количество слоёв (горизонтально)
     * @returns {Asset}
     */
    addAsset(src, name = undefined, w = undefined, h = undefined, f = 1, l = 1) {
        if (!src) return;
        let spriteClass = null, properties = null;
        if (typeof src == 'object' && (arguments.length == 1)) {
            name = src.name || src2name(src.src);
            w = ~~src.width || 0;
            h = ~~src.height || 0;
            f = ~~src.frames || 1;
            l = ~~src.layers || 1;
            spriteClass = src.spriteClass || null;
            properties = src.properties || null;
            src = src.src;
        }

        src = src.replace('%APP.layout.bitmapScale%', '%PATH%/' + this.scale);
        src = src.replace('%PATH%', this.path);

        var asset = new Asset(name, src, w, h, f, l);
        asset.spriteClass = spriteClass;
        asset.scale = this.scale;

        // extend asset with custom properties
        if (properties) {
            for (var prop in properties) {
                if (typeof asset[prop] == 'undefined') asset[prop] = properties[prop];
            }
        }

        this.items.set(name, asset);
        return asset;
    }

    /**
     * Добавляет новый игровой объект в очередь загрузки
     * @param {Object} obj Объект из levels.js
     * @returns {Asset}
     */
    addObject(obj) {
        // objects are always scaled and size is 1x
        var asset = this.addAsset('%APP.layout.bitmapScale%/' + obj.image, obj.name, obj.width * this.scale, obj.height * this.scale, obj.frames, obj.layers);
        if (asset) asset.object = obj;
        return asset;
    }

    /**
     * Получить спрайт по имени
     * @param {String} name Имя спрайта
     * @param {Boolean} [checkLoad=true] Проверка, загружен ли и готов ли к использованию спрайт
     * @returns {Asset}
     */
    getAsset(name, checkLoad = true) {
        let asset = this.items.get(name);

        if (!asset || !asset.bitmap) {
            throw new Error(`Trying to get undefined asset "${name}"`);
        }

        if (checkLoad && !asset.ready) {
            throw new Error(`Trying to get asset "${name}" before it has been loaded`);
        }

        return asset;
    }

    /**
     * Возвращает спрайт в нужном масштабе и размере.
     * @param {String} name Название картинки
     * @param {Object} [params] Стартовые значениея свойств спрайта (например {x:10, y:20, opacity:0.5, onclick:myOnClick})
     * @param {Function} [spriteClass=Sprite] Конструктор спрайта. По умолчанию Sprite. Этот аргумент можно использовать, если требуется инстанциировать объект класса, наследующего Sprite
     * @returns {Sprite}
     */
    getSprite(name, params = null, spriteClass = null) {
        var mc = null, asset = null;
        try {
            asset = this.getAsset(name, true);
        }
        catch (e) {
            asset = new Asset();
        }

        spriteClass = spriteClass || asset.spriteClass || this.spriteClass || Sprite;

        if (typeof spriteClass == "string") {
            if (window[spriteClass]) spriteClass = window[spriteClass];
            else spriteClass = eval(spriteClass);
        }

        if (spriteClass.create && (typeof spriteClass.create == 'function')) {
            mc = spriteClass.create(asset, this);
        }
        else {
            // TODO: remove default sprite constructor usage, or refactor constructor parameters
            mc = new spriteClass(asset.bitmap, asset.width, asset.height, asset.frames, asset.layers);
        }

        if (params && (typeof params == 'object')) {
            // override sprite defaults
            Object.assign(mc, params);
        }

        return mc;
    }

    /**
     * Возвращает bitmap в нужном масштабе и размере.
     * @param {String} name Название картинки
     * @returns {Image}
     */
    getBitmap(name) {
        try {
            var asset = this.getAsset(name, true);
            return asset.bitmap;
        }
        catch (e) {
            return null;
        }
    }
}

export default AssetsLibrary;
